import clsx from "clsx";
import { HTMLAttributes, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Close, Warning, XCircle } from "@/assets/icons/ds";
import { CheckCircle, Info } from "@/assets/icons/icons";

export type NotificationTone = "info" | "warning" | "error" | "success";

const toneClassName: Record<NotificationTone, string> = {
  info: "bg-surface-info",
  warning: "bg-surface-warning",
  error: "bg-surface-error",
  success: "bg-surface-success",
};

const toneFloatingClassName: Record<NotificationTone, string> = {
  info: "bg-surface-info-inverse",
  warning: "bg-surface-warning-inverse",
  error: "bg-surface-error-inverse",
  success: "bg-surface-success-inverse",
};

const toneIcon: Record<NotificationTone, typeof Info> = {
  info: Info,
  warning: Warning,
  error: XCircle,
  success: CheckCircle,
};

const variantClassMapping = {
  light: {
    panel: "bg-surface-quaternary",
    text: "text-foreground-primary",
    description: "text-body-sm",
    badge: toneClassName,
    icon: "text-foreground-primary-inverse",
  },
  dark: {
    panel: "bg-surface-quaternary-inverse",
    text: "text-foreground-primary-inverse",
    description: "text-body-base",
    badge: toneClassName,
    icon: "text-foreground-primary-inverse",
  },
  floating: {
    panel: "bg-surface-primary",
    text: "text-foreground-primary-inverse",
    description: "text-body-sm",
    badge: toneFloatingClassName,
    icon: "text-foreground-primary",
  },
};

interface NotificationProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "title"
> {
  /** Omitted for plain hints that carry only a description, e.g. checkout. */
  title?: ReactNode;
  description?: ReactNode;
  tone?: NotificationTone;
  inverted?: boolean;
  floating?: boolean;
  onClose?: () => void;
  closeLabel?: string;
  dataTestid?: string;
}

/**
 * Notification, an inline panel by default, or a floating toast card when `floating` is set.
 */
export const Notification = ({
  title,
  description,
  tone = "info",
  inverted = false,
  floating = false,
  onClose,
  closeLabel,
  className,
  dataTestid,
  ...rest
}: NotificationProps) => {
  const { t } = useTranslation();
  const Icon = toneIcon[tone];
  const variantClasses =
    variantClassMapping[floating ? "floating" : inverted ? "dark" : "light"];

  return (
    <div
      data-testid={dataTestid}
      className={clsx(
        "flex w-full items-start gap-2 rounded-lg p-2",
        variantClasses.panel,
        className,
      )}
      {...rest}
    >
      <span
        className={clsx(
          "flex w-10 shrink-0 items-center justify-center self-stretch rounded-md",
          variantClasses.icon,
          variantClasses.badge[tone],
        )}
      >
        <Icon data-testid={`notification-icon-${tone}`} className="size-6" />
      </span>
      <div
        className={clsx("flex flex-1 flex-col gap-1 p-2", variantClasses.text)}
      >
        {title && (
          <p className="text-body-base font-semibold leading-md">{title}</p>
        )}
        {/* A div, not a p: callers pass whole fragments in here — the blocked
            call settings send a list — and a ul inside a p is invalid nesting,
            which drops the list out of the styled block. */}
        {description && (
          <div className={clsx("leading-md", variantClasses.description)}>
            {description}
          </div>
        )}
      </div>
      {onClose && (
        <button
          className={clsx(
            "flex size-10 shrink-0 items-center justify-center rounded-md transition-transform hover:rotate-90",
            variantClasses.text,
          )}
          type="button"
          onClick={onClose}
          aria-label={closeLabel ?? t("common.buttons.close")}
        >
          <Close className="size-6" />
        </button>
      )}
    </div>
  );
};
